import React, { useState, useEffect } from "react";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "layouts/Admin.js";
import AuthLayout from "layouts/Auth.js";
import Home from "views/examples/home";
import { useSelector } from "react-redux";
import firebase from "./config/firebase";
import AuthStateManager from "./components/AuthStateManager";
import TermsAndConditions from "views/examples/TaC";
import ConfirmConsert from "views/examples/confirmConsent";
import ShopUser from "views/examples/shop/ShopUser";
import Shop from "views/examples/Shop";
import ShopSign from "views/examples/ShopSign";
import ShopPoster from "views/examples/ShopPoster";
import SignsAndPosters from "views/examples/SignsAndPosters";
import Badges from "views/examples/Badges";
import Trending from "views/examples/Trending";
import SingleChat from "views/examples/1on1Chat";
import GroupChat from "views/examples/GroupChat";
import MEMENTOS from "views/Mementos";
import SeasonalSystem from "views/seasonalSystem";
import Education from "views/examples/Education";
import Blogs from "views/examples/Blogs";
import SchoolLicence from "views/examples/SchoolLicence";
import requestSchoolContracts from "views/examples/requestSchoolContracts";
import SchoolContract from "views/examples/SchoolContract";
import SearchSchoolContract from "views/examples/SearchSchoolContract";
import VerifySeasonalBadges from "views/examples/VerifySeasonalBadges";
import VerifyMomento from "views/examples/VerifyMomento";
import Endorsement from "views/examples/Endorsement";
import Overview from "views/examples/Overview";
import RvRating from "views/examples/AvRating";
import VerifyTalkingBadges from "views/examples/VerifyTalkingBadges";
import VerifyMyBadges from "views/examples/VerifyMyBadges";
import Contests from "views/examples/Contests";
import VerifyEndorseBadge from "views/examples/VerifyEndorseBadge";
import SportBadges from "views/examples/SportBadges";
import CashBack from "views/examples/CashBack";
import Offers from "views/examples/Offers";
import NewsSuggestions from "views/NewsSuggestions";
import Bargains from "views/examples/Bargains";
import * as Sentry from "@sentry/react";

// Initialize Microsoft Clarity only in production
if (process.env.REACT_APP_ENV === "production") {
  (function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
  })(window, document, "clarity", "script", "xm7k3bhsjr");
}

function App() {
  let auth = useSelector((state) => state.auth);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  let redirectPath = urlParams.get("redirect");
  if (redirectPath && (!redirectPath.startsWith("/") || redirectPath.startsWith("//"))) {
    redirectPath = null;
  }

  // Wait for Firebase auth to initialize before rendering routes
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(() => {
      setIsAuthReady(true);
    });

    return () => unsubscribe();
  }, []);

  // Show loading state while auth is initializing
  if (!isAuthReady) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "1.2rem",
          color: "#5e72e4",
        }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  // Get current Firebase user for email verification check
  const currentUser = firebase.auth().currentUser;
  const isEmailVerified = currentUser?.emailVerified || false;

  return (
    <>
      <ToastContainer />
      <BrowserRouter>
        {/* Auth state manager - handles onAuthStateChanged */}
        <AuthStateManager />

        {auth.uid ? (
          // User is authenticated
          <>
            {!isEmailVerified ? (
              // User is authenticated but email not verified
              <Switch>
                <Route
                  path="/auth"
                  render={(props) => <AuthLayout {...props} />}
                />
                {/* Redirect all other routes to email verification */}
                <Redirect to="/auth/email-verification-pending" />
              </Switch>
            ) : (
              // User is authenticated and email verified
              <Switch>
                <Route
                  path="/admin"
                  render={(props) => <AdminLayout {...props} />}
                />
                {redirectPath ? (
                  <Redirect from="/" to={redirectPath} />
                ) : (
                  <Redirect from="/auth" to="/admin/profile" />
                )}
              </Switch>
            )}
          </>
        ) : (
          // User is not authenticated
          <Switch>
            <Route path="/auth" render={(props) => <AuthLayout {...props} />} />
            <Redirect from="/admin" to="/auth/login" />
          </Switch>
        )}

        {/* Public routes - accessible to all users */}
        <Route path="/home" exact component={Home} />
        <Route path="/" exact component={Home} />
        <Route path="/shop" exact component={Shop} />
        <Route path="/1on1Chat" exact component={SingleChat} />
        <Route path="/endorsement" exact component={Endorsement} />
        <Route path="/sport-badges" exact component={SportBadges} />
        <Route path="/groupChat" exact component={GroupChat} />
        <Route path="/overview" exact component={Overview} />
        <Route path="/Momentos" exact component={MEMENTOS} />
        <Route path="/seasonal-system" exact component={SeasonalSystem} />
        <Route path="/talent-contest" exact component={Education} />
        <Route path="/contests" exact component={Contests} />
        <Route path="/school-ip" exact component={requestSchoolContracts} />
        <Route path="/blogs" exact component={Blogs} />
        <Route path="/news-suggestions" exact component={NewsSuggestions} />
        <Route path="/school-licence" exact component={SchoolLicence} />
        <Route path="/verify-endorsements" exact component={Endorsement} />
        <Route path="/trending" exact component={Trending} />
        <Route path="/manager" exact component={RvRating} />
        <Route path="/offers" exact component={Offers} />
        <Route path="/school-contract/:id" exact component={SchoolContract} />
        <Route
          path="/search-school-contract"
          exact
          component={SearchSchoolContract}
        />
        <Route path="/badges" exact component={Badges} />
        <Route path="/shop/signs" exact component={ShopSign} />
        <Route
          path="/shop/signs-and-posters"
          exact
          component={SignsAndPosters}
        />
        <Route path="/shop/posters" exact component={ShopPoster} />
        <Route path="/shop/:name/payment" exact component={ShopUser} />
        <Route path="/terms-and-conditions" component={TermsAndConditions} />
        <Route path="/pizza-money" component={CashBack} />
        <Route path="/bargains" component={Bargains} />
        <Route path="/confirm-consent" exact component={ConfirmConsert} />
        <Route
          path="/verify/seasonal_badges/:id"
          exact
          component={VerifySeasonalBadges}
        />
        <Route path="/verify/momento/:id" exact component={VerifyMomento} />
        <Route
          path="/verify/talking_badges/:id"
          exact
          component={VerifyTalkingBadges}
        />
        <Route path="/verify/my_badges/:id" exact component={VerifyMyBadges} />
        <Route
          path="/verify/endorsment_badges/:id"
          exact
          component={VerifyEndorseBadge}
        />
      </BrowserRouter>
    </>
  );
}

export default process.env.NODE_ENV === "production"
  ? Sentry.withErrorBoundary(App, {
      fallback: (
        <div className="d-flex flex-column align-items-center justify-content-center text-danger py-5">
          <div className="fw-bold text-center fs-1">
            Something went wrong. Please try again later. 🚨
          </div>
        </div>
      ),
    })
  : App;
